import React, { useEffect, useState } from 'react';
import { X, Check, Shield, Copy } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
interface TwoFactorModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function TwoFactorModal({
  isOpen,
  onClose
}: TwoFactorModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'status' | 'setup' | 'verify' | 'complete'>('status');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  useEffect(() => {
    if (isOpen) {
      checkMFAStatus();
    }
  }, [isOpen]);
  const checkMFAStatus = async () => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (user) {
        const factors = user.factors || [];
        const hasTOTP = factors.some(f => f.factor_type === 'totp');
        setIs2FAEnabled(hasTOTP);
      }
    } catch (err) {
      console.error('Error checking MFA status:', err);
    }
  };
  const handleEnroll = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data,
        error
      } = await supabase.auth.mfa.enroll({
        factorType: 'totp'
      });
      if (error) throw error;
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
      setStep('setup');
    } catch (err: any) {
      setError(err.message || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    if (!factorId) {
      setError('Setup not initialized');
      setLoading(false);
      return;
    }
    try {
      const {
        data,
        error
      } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: verifyCode
      });
      if (error) throw error;
      setStep('complete');
      setIs2FAEnabled(true);
      setTimeout(() => {
        onClose();
        resetState();
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };
  const handleDisable = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');
      const factors = user.factors || [];
      const totpFactor = factors.find(f => f.factor_type === 'totp');
      if (totpFactor) {
        const {
          error
        } = await supabase.auth.mfa.unenroll({
          factorId: totpFactor.id
        });
        if (error) throw error;
      }
      setIs2FAEnabled(false);
      onClose();
      resetState();
    } catch (err: any) {
      setError(err.message || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };
  const copySecret = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
    }
  };
  const resetState = () => {
    setStep('status');
    setQrCode(null);
    setSecret(null);
    setVerifyCode('');
    setFactorId(null);
    setError(null);
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-2035">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto shadow-2xl" data-id="element-2036">
        <div className="flex items-center justify-between mb-6" data-id="element-2037">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-2038">
            Two-Factor Authentication
          </h2>
          <button onClick={() => {
          onClose();
          resetState();
        }} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-2039">
            <X size={20} data-id="element-2040" />
          </button>
        </div>
        {step === 'status' && <div className="space-y-4" data-id="element-2041">
            <div className="flex items-center justify-center mb-6" data-id="element-2042">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${is2FAEnabled ? 'bg-green-50' : 'bg-gray-100'}`} data-id="element-2043">
                <Shield size={32} className={is2FAEnabled ? 'text-green-600' : 'text-gray-400'} data-id="element-2044" />
              </div>
            </div>
            <div className="text-center mb-6" data-id="element-2045">
              <h3 className="text-lg font-bold mb-2 text-gray-900" data-id="element-2046">
                {is2FAEnabled ? '2FA is Enabled' : '2FA is Disabled'}
              </h3>
              <p className="text-gray-600 text-sm" data-id="element-2047">
                {is2FAEnabled ? 'Your account is protected with two-factor authentication' : 'Add an extra layer of security to your account'}
              </p>
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800" data-id="element-2048">
                {error}
              </div>}
            {is2FAEnabled ? <button onClick={handleDisable} disabled={loading} className="w-full px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" data-id="element-2049">
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </button> : <button onClick={handleEnroll} disabled={loading} className="w-full px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" data-id="element-2050">
                {loading ? 'Setting up...' : 'Enable 2FA'}
              </button>}
          </div>}
        {step === 'setup' && <div className="space-y-4" data-id="element-2051">
            <div className="text-center mb-4" data-id="element-2052">
              <p className="text-gray-600 text-sm" data-id="element-2053">
                Scan this QR code with your authenticator app
              </p>
            </div>
            {qrCode && <div className="flex justify-center mb-4" data-id="element-2054">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 border-2 border-gray-200 rounded-xl" data-id="element-2055" />
              </div>}
            {secret && <div className="bg-gray-50 rounded-xl p-3 mb-4" data-id="element-2056">
                <p className="text-xs text-gray-600 mb-2" data-id="element-2057">
                  Or enter this code manually:
                </p>
                <div className="flex items-center justify-between" data-id="element-2058">
                  <code className="text-sm font-mono break-all" data-id="element-2059">{secret}</code>
                  <button onClick={copySecret} className="ml-2 p-2 hover:bg-gray-200 rounded-lg flex-shrink-0" data-id="element-2060">
                    <Copy size={16} data-id="element-2061" />
                  </button>
                </div>
              </div>}
            <button onClick={() => setStep('verify')} className="w-full px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors shadow-sm" data-id="element-2062">
              Continue to Verification
            </button>
          </div>}
        {step === 'verify' && <form onSubmit={handleVerify} className="space-y-4" data-id="element-2063">
            <div className="text-center mb-4" data-id="element-2064">
              <p className="text-gray-600 text-sm" data-id="element-2065">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>
            <div data-id="element-2066">
              <input type="text" placeholder="000000" value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required maxLength={6} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-center text-2xl tracking-widest" data-id="element-2067" />
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800" data-id="element-2068">
                {error}
              </div>}
            <div className="flex gap-3 pt-2" data-id="element-2069">
              <button type="button" onClick={() => setStep('setup')} className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-colors" data-id="element-2070">
                Back
              </button>
              <button type="submit" disabled={loading || verifyCode.length !== 6} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" data-id="element-2071">
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>}
        {step === 'complete' && <div className="text-center py-8" data-id="element-2072">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2073">
              <Check size={32} className="text-green-600" data-id="element-2074" />
            </div>
            <h3 className="text-lg font-bold mb-2 text-gray-900" data-id="element-2075">
              2FA Enabled!
            </h3>
            <p className="text-gray-600 text-sm" data-id="element-2076">
              Your account is now protected with two-factor authentication
            </p>
          </div>}
      </div>
    </div>;
}